import { useTranslation } from 'react-i18next';
import { SignInIcon } from '@phosphor-icons/react/dist/csr/SignIn';

import { ChallengeForm } from './ChallengeForm';

/**
 * The lost-phone path: one of the ten codes issued at enrolment, instead of a
 * code from a device that is no longer in the user's hand.
 *
 * ## Two things it deliberately does not do
 *
 * 1. **It does not normalise the code.** The API folds separators and case away
 *    itself — `RecoveryCodeFactory::normalise()` strips everything outside
 *    `[A-Z2-7]` after upper-casing — so doing it here would be a second copy of
 *    that rule, free to drift from the one that actually decides. The value is
 *    sent exactly as the user typed it off their paper.
 * 2. **It does not check the length.** The codes are ten characters, but they
 *    are printed with whatever grouping the user's copy has and read off paper
 *    by somebody who has just lost their phone. A client-side length rule would
 *    refuse a legitimate entry and would be enforcing a format the server does
 *    not require.
 *
 * `autoComplete="off"` and not `one-time-code`: these are printed on paper, not
 * delivered to the device, and offering the platform's SMS-code autofill here
 * would surface the wrong suggestion at the worst moment.
 */

export interface RecoveryCodeScreenProps {
  challengeToken: string;
  /** Rejects with an `ApiError`; the form renders the failure. */
  onRedeem: (challengeToken: string, recoveryCode: string) => Promise<unknown>;
  onUseAuthenticator: () => void;
}

export function RecoveryCodeScreen({
  challengeToken,
  onRedeem,
  onUseAuthenticator,
}: RecoveryCodeScreenProps) {
  const { t } = useTranslation();

  return (
    <ChallengeForm
      title={t('auth.recovery.title')}
      intro={t('auth.recovery.intro')}
      label={t('auth.recovery.codeLabel')}
      hint={t('auth.recovery.codeHint')}
      autoComplete="off"
      canSubmit={(value) => value.trim() !== ''}
      submitLabel={t('auth.signIn')}
      submitIcon={<SignInIcon size={16} />}
      failureMessage={t('auth.recovery.codeRejected')}
      onSubmit={(code) => onRedeem(challengeToken, code)}
      alternate={{ label: t('auth.recovery.useAuthenticator'), onSelect: onUseAuthenticator }}
    />
  );
}
